import { Auth } from "aws-amplify";
import { Cookies } from "react-cookie";

/**
 * Resolve the signed-in user's username for audit fields (updatedBy).
 *
 * Primary source is the AUTH_USER cookie, which the app-boot Hub
 * "signIn" listener sets only during a fresh hosted-UI login. It is a
 * session cookie, so a returning user whose Cognito session is silently
 * restored from localStorage (no fresh sign-in) often has no cookie and
 * would otherwise be blocked from saving ("Unable to determine your
 * identity"). In that case, fall back to the live Amplify session and
 * re-set the cookie (path "/" so it is visible on every route) so
 * subsequent saves read it directly.
 *
 * Returns null when no identity can be determined (not signed in).
 */
export async function getAuthUsername(): Promise<string | null> {
  const cookies = new Cookies();
  const fromCookie = cookies.get("AUTH_USER");
  if (fromCookie) {
    return fromCookie;
  }
  try {
    const user = await Auth.currentAuthenticatedUser();
    if (user && user.username) {
      cookies.set("AUTH_USER", user.username, { path: "/" });
      return user.username;
    }
    return null;
  } catch (e) {
    // Expected when there is no session (Auth throws its "not
    // authenticated" sentinel). Logged so a genuine Amplify failure
    // is distinguishable from a signed-out user when debugging.
    console.debug("getAuthUsername: no authenticated session", e);
    return null;
  }
}
